import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Tabs from './shared/Tabs';
import { ChevronDown, Link2 } from 'lucide-react';
import CopyButton from './shared/CopyButton';
import legacyIngressService from '../services/legacyIngressService';
import './OxiDequeueAdmin.css';
import './AdminSyncConnectQueries.css';
import './shared/Disclosure.css';

// ADMIN › INFRA › LEGACY INGRESS — the legacy stack's traffic, one tab per direction.
//   Ingress  what still calls the legacy stack, one row per (legacy entry lambda, API key): what the key resolved to on
//            its latest call, the outcome, and when it was last seen (legacy_source_activity on the legacy database).
//            The lambdas batch their writes, so Last seen can trail by up to a minute and Calls is approximate.
//   OHIP     who the legacy stack CALLS: every connector hc-ohipscheduler-run starts (its own query, so the list is
//            right before any connector reports anything), beside what that connector recorded about its calls to
//            OHIP (legacy_target_activity, on the hub database — V626). A connector whose code has not been changed yet shows no values.
// Step 1 of moving clients to the hub. Rows can be corrected by hand; an API key is shown on request (audited).

const AUTO_REFRESH_MS = 3000;
const INGRESS_FIELDS = [
    { key: 'source_lambda', label: 'Source lambda' },
    { key: 'api_key', label: 'API key (leave empty to keep it)', secret: true },
    { key: 'connectorid', label: 'Connector id', type: 'number' },
    { key: 'accountid', label: 'Account id', type: 'number' },
    { key: 'outcome', label: 'Outcome', options: true },
    { key: 'last_property', label: 'Last property' },
    { key: 'last_message_type', label: 'Last message type' },
    { key: 'last_ip', label: 'Last IP' },
    { key: 'hit_count', label: 'Calls', type: 'number' },
    { key: 'first_seen', label: 'First seen', type: 'datetime-local' },
    { key: 'last_seen', label: 'Last seen', type: 'datetime-local' },
];
const OHIP_FIELDS = [
    { key: 'outcome', label: 'Outcome', options: true, blank: true },
    { key: 'last_property', label: 'Last property (hotel)' },
    { key: 'last_message_type', label: 'Last record type' },
    { key: 'last_endpoint', label: 'Last endpoint' },
    { key: 'last_host', label: 'Gateway host' },
    { key: 'last_error', label: 'Last error' },
    { key: 'hit_count', label: 'Calls', type: 'number' },
    { key: 'item_count', label: 'Events', type: 'number' },
    { key: 'first_seen', label: 'First seen', type: 'datetime-local' },
    { key: 'last_seen', label: 'Last seen', type: 'datetime-local' },
];
const errText = (e) => e.response?.data?.error?.message || e.response?.data?.message || e.message;
const when = (v) => (v ? new Date(v).toLocaleString() : '—');
const ago = (v) => {
    if (!v) return '';
    const s = Math.max(0, Math.round((Date.now() - new Date(v).getTime()) / 1000));
    return s < 90 ? `${s}s ago` : s < 5400 ? `${Math.round(s / 60)}m ago` : s < 172800 ? `${Math.round(s / 3600)}h ago` : `${Math.round(s / 86400)}d ago`;
};
// A DATETIME from the API (an ISO string) as a datetime-local input value, and back.
const toInput = (v) => {
    if (!v) return '';
    const d = new Date(v);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};
const fromInput = (v) => (v ? new Date(v).toISOString().slice(0, 19).replace('T', ' ') : '');
const OUTCOME_CLASS = {
    OK: 'returned', INVALID_KEY: 'error', IP_REJECTED: 'queued', TYPE_REJECTED: 'queued',
    AUTH_FAILED: 'error', ERROR: 'error', TIMEOUT: 'queued', FORWARDED: 'returned',
};
const Outcome = ({ value, title }) => (value
    ? <span className={`scq-status scq-status-${OUTCOME_CLASS[value] || 'queued'}`} title={title || undefined}>{value}</span>
    : '—');
const LastSeen = ({ value }) => <>{when(value)}<div className="scq-meta">{ago(value)}</div></>;

const formOf = (fields, row) => Object.fromEntries(fields.map(f => [f.key,
    f.secret ? '' : f.type === 'datetime-local' ? toInput(row[f.key]) : (row[f.key] ?? '')]));

// Only what changed, so the audit trail records exactly what was edited. A secret field is sent only when typed.
const changedBody = (fields, form, row) => {
    const body = {};
    for (const f of fields) {
        if (f.secret) { if (String(form[f.key]).trim()) body[f.key] = String(form[f.key]).trim(); continue; }
        const now = f.type === 'datetime-local' ? fromInput(form[f.key]) : form[f.key];
        const was = f.type === 'datetime-local' ? fromInput(toInput(row[f.key])) : (row[f.key] ?? '');
        if (String(now) !== String(was)) body[f.key] = now;
    }
    return body;
};

// One loader per tab: a slow answer must not stack up behind the 3-second timer; `quiet` keeps the table on screen.
function useLoader(fetcher) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const inFlight = useRef(false);
    const load = useCallback(async ({ quiet } = {}) => {
        if (inFlight.current) return;
        inFlight.current = true;
        if (!quiet) setLoading(true);
        try {
            setData((await fetcher()).data);
            setError(null);
        } catch (e) {
            setError(errText(e));
        } finally {
            inFlight.current = false;
            setLoading(false);
        }
    }, [fetcher]);
    useEffect(() => { load(); }, [load]);
    return { data, loading, error, setError, load };
}

// AUTO REFRESH — every 3 seconds while ticked, paused while a row is being edited so the form is not disturbed.
function useAutoRefresh(autoRefresh, paused, load) {
    useEffect(() => {
        if (!autoRefresh || paused) return undefined;
        const id = setInterval(() => load({ quiet: true }), AUTO_REFRESH_MS);
        return () => clearInterval(id);
    }, [autoRefresh, paused, load]);
}

// The page's Refresh button bumps `tick`; the open tab reloads on it (not on mount — useLoader already did).
function useRefreshTick(tick, load) {
    // The tick last acted on: a new `load` (the filters changed) must not count as a press of Refresh.
    const seen = useRef(tick);
    useEffect(() => {
        if (seen.current === tick) return;
        seen.current = tick;
        load();
    }, [tick, load]);
}

// The editor both tabs use. `editing` is { row, form, saving, error }; onSave(body) does the PUT.
function EditRowModal({ title, fields, outcomes, editing, setEditing, onSave, onSaved }) {
    const setField = (key, value) => setEditing(e => ({ ...e, form: { ...e.form, [key]: value } }));
    const save = async () => {
        const body = changedBody(fields, editing.form, editing.row);
        if (!Object.keys(body).length) { setEditing(null); return; }
        setEditing(e => ({ ...e, saving: true, error: null }));
        try {
            await onSave(editing.row, body);
            setEditing(null);
            onSaved && onSaved(body);
        } catch (e) {
            setEditing(x => ({ ...x, saving: false, error: errText(e) }));
        }
    };
    return (
        <div className="odq-modal-overlay" onClick={() => !editing.saving && setEditing(null)}>
            <div className="odq-modal scq-compose" onClick={e => e.stopPropagation()}>
                <div className="odq-modal-head">
                    <span>{title}</span>
                    <button className="acp-btn acp-btn-secondary acp-btn-sm" onClick={() => setEditing(null)} disabled={editing.saving}>Close</button>
                </div>
                <div className="odq-modal-body scq-form">
                    {fields.map(f => (
                        <label key={f.key}>{f.label}
                            {f.options
                                ? <select value={editing.form[f.key]} onChange={e => setField(f.key, e.target.value)}>
                                    {f.blank && <option value="">—</option>}
                                    {outcomes.map(o => <option key={o} value={o}>{o}</option>)}
                                  </select>
                                : <input type={f.type || 'text'} step={f.type === 'datetime-local' ? 1 : undefined}
                                         value={editing.form[f.key]} onChange={e => setField(f.key, e.target.value)} />}
                        </label>
                    ))}
                    {editing.error && <div className="os-msg os-msg-error">{editing.error}</div>}
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                        <button className="acp-btn acp-btn-secondary" onClick={() => setEditing(null)} disabled={editing.saving}>Cancel</button>
                        <button className="acp-btn acp-btn-primary" onClick={save} disabled={editing.saving}>{editing.saving ? 'Saving…' : 'Save'}</button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function IngressTab({ autoRefresh, tick }) {
    const [filters, setFilters] = useState({ sourceLambda: '', outcome: '', search: '' });
    const [keys, setKeys] = useState({});          // id -> decrypted API key, once revealed
    const [editing, setEditing] = useState(null);
    const fetcher = useCallback(
        () => legacyIngressService.list(Object.fromEntries(Object.entries(filters).filter(([, v]) => v))), [filters]);
    const { data, loading, error, setError, load } = useLoader(fetcher);
    useAutoRefresh(autoRefresh, !!editing, load);
    useRefreshTick(tick, load);

    const rows = data?.rows || [];
    const sourceLambdas = data?.sourceLambdas || [];
    const outcomes = data?.outcomes || [];
    const checkedAt = data?.checkedAt;

    const reveal = async (id) => {
        try {
            const res = (await legacyIngressService.revealKey(id)).data;
            setKeys(k => ({ ...k, [id]: res.apiKey }));
        } catch (e) {
            setError(errText(e));
        }
    };

    const active = rows.filter(r => r.outcome === 'OK');
    const connectors = new Set(active.map(r => r.connectorid).filter(Boolean)).size;

    return (
        <>
            {error && <div className="os-msg os-msg-error" onClick={() => setError(null)}>{error}</div>}
            <div className="acp-card">
                <div className="acp-card-header" style={{ flexWrap: 'wrap', gap: '0.5rem' }}>
                    <span className="acp-card-title">
                        {rows.length} source{rows.length === 1 ? '' : 's'} · {connectors} active connector{connectors === 1 ? '' : 's'}
                    </span>
                    <select className="odq-filter-select" value={filters.sourceLambda}
                            onChange={e => setFilters(f => ({ ...f, sourceLambda: e.target.value }))}>
                        <option value="">All source lambdas</option>
                        {sourceLambdas.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <select className="odq-filter-select" value={filters.outcome}
                            onChange={e => setFilters(f => ({ ...f, outcome: e.target.value }))}>
                        <option value="">All outcomes</option>
                        {outcomes.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                    <input className="odq-filter-select" placeholder="Connector, account, property, IP…" value={filters.search}
                           onChange={e => setFilters(f => ({ ...f, search: e.target.value }))} />
                    {checkedAt && <span className="scq-meta" style={{ marginLeft: 'auto' }}>Updated {new Date(checkedAt).toLocaleTimeString()}</span>}
                </div>
                {loading && !rows.length ? <div className="acp-empty">Loading…</div>
                    : rows.length === 0 ? <div className="acp-empty">Nothing recorded yet.</div>
                    : (
                    <table className="acp-table">
                        <thead>
                            <tr>
                                <th>Source lambda</th><th>API key</th><th>Outcome</th><th>Connector</th><th>Account</th>
                                <th>Last property / type</th><th>Last IP</th><th>Calls</th><th>First seen</th><th>Last seen</th><th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(r => (
                                <tr key={r.id}>
                                    <td className="scq-mono">{r.source_lambda}</td>
                                    <td className="scq-mono" style={{ whiteSpace: 'nowrap' }}>
                                        {keys[r.id] != null
                                            ? keys[r.id]
                                            : <>…{r.api_key_hint || '????'} <button className="acp-btn acp-btn-secondary acp-btn-sm" onClick={() => reveal(r.id)}>Show</button></>}
                                    </td>
                                    <td><Outcome value={r.outcome} /></td>
                                    <td>{r.connectorid
                                        ? <>{r.connectorid} · {r.connector_name || '?'}<div className="scq-meta">{[r.system_name, r.connector_status].filter(Boolean).join(' · ')}</div></>
                                        : '—'}</td>
                                    <td>{r.accountid ? `${r.accountid} · ${r.account_name || '?'}` : '—'}</td>
                                    <td>{r.last_property || '—'}<div className="scq-meta">{r.last_message_type || ''}</div></td>
                                    <td className="scq-mono">{r.last_ip || '—'}</td>
                                    <td>{Number(r.hit_count || 0).toLocaleString()}</td>
                                    <td>{when(r.first_seen)}</td>
                                    <td style={{ whiteSpace: 'nowrap' }}><LastSeen value={r.last_seen} /></td>
                                    <td><button className="acp-btn acp-btn-secondary acp-btn-sm"
                                                onClick={() => setEditing({ row: r, form: formOf(INGRESS_FIELDS, r), saving: false, error: null })}>Edit</button></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {editing && (
                <EditRowModal
                    title={`Legacy ingress row ${editing.row.id}`}
                    fields={INGRESS_FIELDS}
                    outcomes={outcomes}
                    editing={editing}
                    setEditing={setEditing}
                    onSave={(row, body) => legacyIngressService.update(row.id, body)}
                    onSaved={(body) => {
                        if (body.api_key) setKeys(k => { const n = { ...k }; delete n[editing.row.id]; return n; });
                        load({ quiet: true });
                    }}
                />
            )}
        </>
    );
}

// The folder the OHIP connectors' code is distilled into, deployed to every function at once, on Node 22.
const GENERIC_OHIP_SRC = './legacy-code/sqs_GENERIC_ORACLEOHIP';
const GENERIC_OHIP_RUNTIME = 'nodejs22.x';
// Not on the generic code: Rotana's connectors also take OXI messages in (OXINEWMESSAGE), so they keep their own build.
const NOT_GENERIC = new Set([3293, 3316]);

// The scheduler's trigger types, shortened for the cell.
const TRIGGER_LABEL = { FETCHBUSINESSEVENTS: 'Business events', FETCHSTATISTICS: 'Statistics' };

// A connector's hub link as one line: partner › chain › hotel, marked when it stops short of a hotel.
const hubPathOf = (l) => (l ? [l.partnerName, l.accountName, l.hotelName].filter(Boolean).join(' › ') : '');

function OhipTab({ autoRefresh, tick }) {
    const navigate = useNavigate();
    const [filters, setFilters] = useState({ outcome: '', search: '' });
    const [editing, setEditing] = useState(null);
    const [showCommands, setShowCommands] = useState(false);
    const fetcher = useCallback(() => legacyIngressService.listOhip(), []);
    const { data, loading, error, setError, load } = useLoader(fetcher);
    useAutoRefresh(autoRefresh, !!editing, load);
    useRefreshTick(tick, load);

    const all = data?.rows || [];
    const outcomes = data?.outcomes || [];
    const checkedAt = data?.checkedAt;
    const activityTable = data ? data.activityTable : true;
    const linked = all.filter(r => r.link).length;

    // A few dozen connectors at most, so the filters work on what is loaded.
    const q = filters.search.trim().toLowerCase();
    const rows = all.filter(r =>
        (!filters.outcome || (filters.outcome === '-' ? !r.outcome : r.outcome === filters.outcome))
        && (!q || [r.connectorid, r.connector_name, r.account_name, r.accountid, r.source_lambda, r.last_property, r.last_host,
                   r.hotel_code, hubPathOf(r.link)]
            .some(v => v != null && String(v).toLowerCase().includes(q))));
    const scheduled = all.filter(r => r.scheduled).length;
    const reporting = all.filter(r => r.last_seen).length;
    // The repo-root Makefile's legacy targets, for the connectors listed (so the filters choose which).
    const sorted = [...rows].sort((a, b) => a.connectorid - b.connectorid);
    const namesOf = (list) => [...new Set(list.map(r => r.source_lambda).filter(Boolean))].join(',');
    const lambdas = namesOf(sorted);
    const genericLambdas = namesOf(sorted.filter(r => !NOT_GENERIC.has(r.connectorid)));
    const lambdaCount = lambdas ? lambdas.split(',').length : 0;
    const commands = [
        ...(lambdas ? [{ label: 'Fetch', line: `make env=prd lambda-fetch lambda=${lambdas}` }] : []),
        ...(genericLambdas ? [{ label: 'Deploy', line: `make env=prd lambda-deploy src=${GENERIC_OHIP_SRC} lambda=${genericLambdas} runtime=${GENERIC_OHIP_RUNTIME}` }] : []),
    ];

    return (
        <>
            {error && <div className="os-msg os-msg-error" onClick={() => setError(null)}>{error}</div>}
            {!activityTable && (
                <div className="os-msg">
                    legacy_target_activity does not exist yet (flyway V626 has not run),
                    so the connectors are listed without values and cannot be edited.
                </div>
            )}
            {commands.length > 0 && (
                <div style={{ marginBottom: '1rem' }}>
                    <button type="button" className="hc-disclosure" aria-expanded={showCommands}
                            onClick={() => setShowCommands(v => !v)}>
                        <span className="hc-disclosure-title">Fetch and deploy</span>
                        <span className="hc-disclosure-count">
                            {lambdaCount} lambda{lambdaCount === 1 ? '' : 's'} listed below — filter the table to narrow them
                        </span>
                        <span className="hc-disclosure-action">
                            {showCommands ? 'Hide' : 'Show'}
                            <ChevronDown size={16} className="hc-disclosure-chevron" />
                        </span>
                    </button>
                    {showCommands && (
                        <div className="acp-card hc-disclosure-panel">
                            <p className="scq-meta" style={{ padding: '0.75rem 1.5rem 0', margin: 0 }}>
                                Deploy leaves out Rotana (3293, 3316 — they keep their own build), sets the runtime after the code,
                                checks every function first and deploys nothing if one is refused (force=1 for functions the folder
                                has not seen yet), then asks once — type the number of functions — unless yes=1.
                            </p>
                            {commands.map(c => (
                                <div key={c.label} style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start', padding: '0.75rem 1.5rem' }}>
                                    <strong style={{ minWidth: '4rem' }}>{c.label}</strong>
                                    <code className="scq-mono" style={{ flex: 1, wordBreak: 'break-all' }}>{c.line}</code>
                                    <CopyButton value={c.line} label="Copy" />
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            <div className="acp-card">
                <div className="acp-card-header" style={{ flexWrap: 'wrap', gap: '0.5rem' }}>
                    <span className="acp-card-title">
                        {scheduled} scheduled connector{scheduled === 1 ? '' : 's'} · {reporting} reporting
                    </span>
                    <select className="odq-filter-select" value={filters.outcome}
                            onChange={e => setFilters(f => ({ ...f, outcome: e.target.value }))}>
                        <option value="">All outcomes</option>
                        <option value="-">Not reporting</option>
                        {outcomes.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                    <input className="odq-filter-select" placeholder="Connector, account, lambda, hotel…" value={filters.search}
                           onChange={e => setFilters(f => ({ ...f, search: e.target.value }))} />
                    {checkedAt && <span className="scq-meta" style={{ marginLeft: 'auto' }}>Updated {new Date(checkedAt).toLocaleTimeString()}</span>}
                    <span className="scq-meta">{linked} of {all.length} linked to the hub</span>
                    <button className="acp-btn acp-btn-secondary acp-btn-sm" onClick={() => navigate('/admin/legacy-ingress/ohip/link')}>
                        <Link2 size={14} /> Link to hub
                    </button>
                </div>
                {loading && !all.length ? <div className="acp-empty">Loading…</div>
                    : rows.length === 0 ? <div className="acp-empty">{all.length ? 'No connector matches.' : 'hc-ohipscheduler-run would start no connector.'}</div>
                    : (
                    <table className="acp-table">
                        <thead>
                            <tr>
                                <th>Source lambda</th><th>Triggers</th><th>Outcome</th><th>Connector</th><th>Account</th><th>Hub</th>
                                <th>Last property / type</th><th>Last endpoint</th><th>Calls</th><th>Events</th>
                                <th>First seen</th><th>Last seen</th><th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(r => (
                                <tr key={r.connectorid}>
                                    <td className="scq-mono">{r.source_lambda || '—'}</td>
                                    <td>
                                        {r.scheduled
                                            ? (r.trigger_types || '').split(',').filter(Boolean).map(t => TRIGGER_LABEL[t] || t).join(', ')
                                            : 'Not scheduled'}
                                        <div className="scq-meta">
                                            {r.scheduled ? `Last scheduled ${r.last_scheduled ? when(String(r.last_scheduled).replace(' ', 'T') + 'Z') : 'never'}` : 'calls recorded, no scheduled trigger'}
                                        </div>
                                    </td>
                                    <td>
                                        <Outcome value={r.outcome} title={r.last_error} />
                                        {r.last_error && <div className="scq-meta" title={r.last_error}>{r.last_error.slice(0, 60)}{r.last_error.length > 60 ? '…' : ''}</div>}
                                    </td>
                                    <td>{r.connectorid} · {r.connector_name || '?'}
                                        <div className="scq-meta">{['ORACLEOHIP', r.connector_status, r.hotel_code && `HOTELID ${r.hotel_code}`].filter(Boolean).join(' · ')}</div></td>
                                    <td>{r.accountid ? `${r.accountid} · ${r.account_name || '?'}` : '—'}</td>
                                    <td>{r.link ? hubPathOf(r.link) : <span className="scq-meta">not linked</span>}
                                        {r.link && r.link.entityType !== 'hotel' && <div className="scq-meta">{r.link.entityType === 'account' ? 'chain only' : 'partner only'}</div>}</td>
                                    <td>{r.last_property || '—'}<div className="scq-meta">{r.last_message_type || ''}</div></td>
                                    <td className="scq-mono">{r.last_endpoint || '—'}<div className="scq-meta">{r.last_host || ''}</div></td>
                                    <td>{r.last_seen ? Number(r.hit_count || 0).toLocaleString() : '—'}</td>
                                    <td>{r.last_seen ? Number(r.item_count || 0).toLocaleString() : '—'}</td>
                                    <td>{when(r.first_seen)}</td>
                                    <td style={{ whiteSpace: 'nowrap' }}><LastSeen value={r.last_seen} /></td>
                                    <td><button className="acp-btn acp-btn-secondary acp-btn-sm" disabled={!activityTable}
                                                onClick={() => setEditing({ row: r, form: formOf(OHIP_FIELDS, r), saving: false, error: null })}>Edit</button></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {editing && (
                <EditRowModal
                    title={`OHIP calls of connector ${editing.row.connectorid}`}
                    fields={OHIP_FIELDS}
                    outcomes={outcomes}
                    editing={editing}
                    setEditing={setEditing}
                    onSave={(row, body) => legacyIngressService.updateOhip(row.connectorid, body)}
                    onSaved={() => load({ quiet: true })}
                />
            )}
        </>
    );
}

const TABS = [
    { key: 'ingress', label: 'Ingress' },
    { key: 'ohip', label: 'OHIP Reader' },
];

export default function AdminLegacyIngress() {
    const [searchParams, setSearchParams] = useSearchParams();
    const tab = TABS.some(t => t.key === searchParams.get('tab')) ? searchParams.get('tab') : 'ingress';
    const [autoRefresh, setAutoRefresh] = useState(false);
    const [tick, setTick] = useState(0);

    return (
        <div className="acp-page">
            <div className="acp-header">
                <div>
                    <h1 className="acp-title">Legacy Ingress</h1>
                    {tab === 'ingress' ? (
                        <p className="acp-subtitle">
                            What still calls the legacy stack: one row per legacy entry lambda and API key, with the connector
                            and account the key resolved to on its latest call. Calls that failed authentication are kept too.
                            The lambdas write at most once a minute per key, so Last seen can trail by up to a minute and Calls is approximate.
                        </p>
                    ) : (
                        <p className="acp-subtitle">
                            Who the legacy stack calls for data: every connector hc-ohipscheduler-run starts (every 5 minutes,
                            for its scheduled business-events and statistics triggers), with what that connector recorded about
                            its calls to OHIP. A connector shows values once its code records them.
                        </p>
                    )}
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <label style={{ display: 'flex', gap: '0.35rem', alignItems: 'center', whiteSpace: 'nowrap' }}>
                        <input type="checkbox" checked={autoRefresh} onChange={e => setAutoRefresh(e.target.checked)} />
                        Auto refresh (3s)
                    </label>
                    <button className="acp-btn acp-btn-secondary" onClick={() => setTick(t => t + 1)}>Refresh</button>
                </div>
            </div>

            <Tabs tabs={TABS} active={tab} onChange={(key) => {
                // REPLACE, and carry the other parameters through (STYLE.md › Tabs).
                const next = new URLSearchParams(searchParams);
                next.set('tab', key);
                setSearchParams(next, { replace: true });
            }} />

            {tab === 'ingress'
                ? <IngressTab autoRefresh={autoRefresh} tick={tick} />
                : <OhipTab autoRefresh={autoRefresh} tick={tick} />}
        </div>
    );
}
